import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Search, Package, Edit2, Trash2, Eye, EyeOff,
  X, Save, Upload, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ImageUploader from '@/components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  { id: 'vitella', name: 'Vitella', icon: '🥩', visible: true },
  { id: 'maiale', name: 'Maiale', icon: '🐖', visible: true },
  { id: 'pollo', name: 'Pollo', icon: '🐔', visible: true },
  { id: 'preparati', name: 'Preparati', icon: '🍔', visible: true },
  { id: 'salame', name: 'Salame', icon: '🥓', visible: true },
  { id: 'pane', name: 'Pane', icon: '🥖', visible: true },
  { id: 'vino', name: 'Vino', icon: '🍷', visible: true },
  { id: 'uova', name: 'Uova', icon: '🥚', visible: true }
];

const emptyForm = {
  name: '',
  description: '',
  price_per_unit: '',
  unit: 'kg',
  category: 'vitella',
  min_quantity: 0.1,
  image_url: '',
  available: true
};

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [productsResponse, settingsResponse] = await Promise.all([
        axios.get(`${API}/admin/products`, authHeaders()),
        axios.get(`${API}/admin/settings`, authHeaders())
      ]);
      setProducts(productsResponse.data || []);
      const configured = settingsResponse.data?.product_categories;
      if (Array.isArray(configured) && configured.length) {
        setCategories(configured
          .map((item, index) => ({
            ...item,
            visible: item.visible !== false,
            order: Number.isFinite(Number(item.order)) ? Number(item.order) : index
          }))
          .sort((a, b) => a.order - b.order));
      }
    } catch (error) {
      toast.error('Impossibile caricare i prodotti');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return products.filter(product => {
      const matchesCategory = category === 'all' || product.category === category;
      const matchesQuery = !normalized
        || String(product.name || '').toLowerCase().includes(normalized)
        || String(product.description || '').toLowerCase().includes(normalized);
      return matchesCategory && matchesQuery;
    });
  }, [products, query, category]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const item of products) counts[item.category] = (counts[item.category] || 0) + 1;
    return counts;
  }, [products]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: categories.find(item => item.visible !== false)?.id || 'vitella' });
    setShowModal(true);
  };

  const openEdit = product => {
    setEditing(product);
    setForm({
      name: product.name || '',
      description: product.description || '',
      price_per_unit: product.price_per_unit ?? '',
      unit: product.unit || 'kg',
      category: product.category || 'vitella',
      min_quantity: product.min_quantity ?? (product.unit === 'kg' ? 0.1 : 1),
      image_url: product.image_url || '',
      available: product.available !== false
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const saveProduct = async () => {
    if (!form.name.trim()) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    if (!form.price_per_unit || Number(form.price_per_unit) < 0) {
      toast.error('Inserisci un prezzo valido');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing.id}`, {
          ...form,
          price_per_unit: Number(form.price_per_unit),
          min_quantity: Number(form.min_quantity || (form.unit === 'kg' ? 0.1 : 1))
        }, authHeaders());
        toast.success('Prodotto aggiornato');
      } else {
        const body = new FormData();
        body.append('name', form.name.trim());
        body.append('description', form.description || '');
        body.append('price_per_unit', String(Number(form.price_per_unit)));
        body.append('unit', form.unit);
        body.append('category', form.category);
        body.append('min_quantity', String(Number(form.min_quantity || (form.unit === 'kg' ? 0.1 : 1))));
        body.append('image_url', form.image_url || '');
        await axios.post(`${API}/admin/products`, body, authHeaders());
        toast.success('Prodotto creato');
      }
      closeModal();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async product => {
    try {
      await axios.put(
        `${API}/admin/products/${product.id}/availability`,
        { available: product.available === false },
        authHeaders()
      );
      fetchProducts();
    } catch (error) {
      toast.error('Impossibile cambiare la disponibilità');
    }
  };

  const deleteProduct = async product => {
    if (!window.confirm(`Eliminare "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.id}`, authHeaders());
      toast.success('Prodotto eliminato');
      fetchProducts();
    } catch (error) {
      toast.error('Impossibile eliminare il prodotto');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#2C2C2C]">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E7E0D6]">
        <div className="max-w-6xl mx-auto px-3 sm:px-5 py-3 flex items-center gap-3">
          <Link to="/admin/site-editor" className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center">
            <ArrowLeft size={20} />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-xl sm:text-2xl font-bold truncate">Catalogo prodotti</h1>
            <p className="text-xs sm:text-sm text-[#6F675F]">{products.length} prodotti totali</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="h-10 px-3 sm:px-4 rounded-xl bg-[#5D1822] text-white font-semibold flex items-center gap-2"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Nuovo prodotto</span>
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-3 sm:px-5 py-4 sm:py-6">
        <div className="sticky top-[65px] z-20 bg-[#F7F4EE] pb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8C847A]" size={19} />
            <input
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Cerca un prodotto..."
              className="w-full h-12 pl-12 pr-4 rounded-2xl border border-[#DDD6CC] bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#5D1822]/20"
            />
          </div>

          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setCategory('all')}
              className={`h-10 px-4 rounded-xl whitespace-nowrap font-semibold border ${
                category === 'all'
                  ? 'bg-[#5D1822] text-white border-[#5D1822]'
                  : 'bg-white border-[#DDD6CC]'
              }`}
            >
              Tutti ({products.length})
            </button>
            {categories.map(item => (
              <button
                type="button"
                key={item.id}
                onClick={() => setCategory(item.id)}
                className={`h-10 px-4 rounded-xl whitespace-nowrap font-semibold border ${
                  category === item.id
                    ? 'bg-[#5D1822] text-white border-[#5D1822]'
                    : 'bg-white border-[#DDD6CC]'
                }`}
              >
                {item.icon} {item.name} ({categoryCounts[item.id] || 0})
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-[#6F675F]">Caricamento prodotti...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Package className="mx-auto mb-3 text-[#B7AEA4]" size={42} />
            <p className="font-semibold">Nessun prodotto trovato</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(product => {
              const categoryInfo = categories.find(item => item.id === product.category);
              return (
                <article
                  key={product.id}
                  className={`bg-white border border-[#E6DFD5] rounded-2xl overflow-hidden flex items-stretch ${
                    product.available === false ? 'opacity-60' : ''
                  }`}
                >
                  {product.image_url ? (
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-20 sm:w-28 object-cover bg-[#F0ECE6]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-20 sm:w-28 bg-gradient-to-br from-[#5D1822] to-[#3A0F15] flex items-center justify-center">
                      <Package className="text-[#C5A059]" size={30} />
                    </div>
                  )}

                  <div className="min-w-0 flex-1 p-3 sm:p-4">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <h2 className="font-bold text-base sm:text-lg truncate">{product.name}</h2>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-[#5D1822] font-bold">
                            €{Number(product.price_per_unit || 0).toFixed(2).replace('.', ',')}/{product.unit}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-[#F2EEE7]">
                            {categoryInfo?.icon} {categoryInfo?.label || product.category}
                          </span>
                          {product.available === false && (
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-semibold">
                              Nascosto
                            </span>
                          )}
                        </div>
                        {product.description && (
                          <p className="hidden sm:block mt-2 text-sm text-[#6F675F] line-clamp-1">
                            {product.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleAvailability(product)}
                          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                            product.available === false
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                          title={product.available === false ? 'Mostra prodotto' : 'Nascondi prodotto'}
                        >
                          {product.available === false ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(product)}
                          className="w-9 h-9 rounded-xl bg-[#F2EEE7] flex items-center justify-center"
                          title="Modifica"
                        >
                          <Edit2 size={17} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteProduct(product)}
                          className="w-9 h-9 rounded-xl bg-red-100 text-red-700 flex items-center justify-center"
                          title="Elimina"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/70 p-3 sm:p-6 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl">
            <div className="sticky top-0 bg-white border-b border-[#E7E0D6] px-4 sm:px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-serif text-xl font-bold">
                  {editing ? 'Modifica prodotto' : 'Nuovo prodotto'}
                </h2>
                <p className="text-sm text-[#6F675F]">Compila solo i dati necessari</p>
              </div>
              <button type="button" onClick={closeModal} className="w-10 h-10 rounded-xl bg-[#F2EEE7] flex items-center justify-center">
                <X size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4">
              <label className="block">
                <span className="block text-sm font-semibold mb-2">Nome prodotto *</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={event => setForm(previous => ({ ...previous, name: event.target.value }))}
                  className="w-full h-12 px-4 rounded-xl border border-[#DDD6CC]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-semibold mb-2">Descrizione</span>
                <textarea
                  value={form.description}
                  onChange={event => setForm(previous => ({ ...previous, description: event.target.value }))}
                  className="w-full min-h-[90px] px-4 py-3 rounded-xl border border-[#DDD6CC] resize-y"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span className="block text-sm font-semibold mb-2">Prezzo *</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={form.price_per_unit}
                    onChange={event => setForm(previous => ({ ...previous, price_per_unit: event.target.value }))}
                    className="w-full h-12 px-4 rounded-xl border border-[#DDD6CC]"
                  />
                </label>

                <label>
                  <span className="block text-sm font-semibold mb-2">Unità</span>
                  <select
                    value={form.unit}
                    onChange={event => setForm(previous => ({
                      ...previous,
                      unit: event.target.value,
                      min_quantity: event.target.value === 'kg' ? 0.1 : 1
                    }))}
                    className="w-full h-12 px-4 rounded-xl border border-[#DDD6CC] bg-white"
                  >
                    <option value="kg">kg</option>
                    <option value="pz">pezzo</option>
                    <option value="bottiglia">bottiglia</option>
                    <option value="confezione">confezione</option>
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="block text-sm font-semibold mb-2">Categoria</span>
                <select
                  value={form.category}
                  onChange={event => setForm(previous => ({ ...previous, category: event.target.value }))}
                  className="w-full h-12 px-4 rounded-xl border border-[#DDD6CC] bg-white"
                >
                  {categories.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.icon} {item.name}
                    </option>
                  ))}
                </select>
              </label>

              <div>
                <span className="block text-sm font-semibold mb-2">Immagine prodotto</span>
                <ImageUploader
                  value={form.image_url}
                  onChange={value => setForm(previous => ({ ...previous, image_url: value || '' }))}
                  folder="products"
                />
              </div>

              <label className="flex items-center gap-3 rounded-xl bg-[#F7F4EE] px-4 py-3">
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={event => setForm(previous => ({ ...previous, available: event.target.checked }))}
                  className="w-5 h-5"
                />
                <span className="font-semibold">Prodotto visibile ai clienti</span>
              </label>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#E7E0D6] p-4 sm:px-6 flex gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="flex-1 h-12 rounded-xl border border-[#DDD6CC] font-semibold"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={saveProduct}
                disabled={saving}
                className="flex-1 h-12 rounded-xl bg-[#5D1822] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Save size={18} />
                {saving ? 'Salvataggio...' : 'Salva prodotto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
